import { Label } from "@tql/ui-components-react/kendo/labels";
import { Checkbox, Input } from "@tql/ui-components-react/tql/inputs/beta";

import {
	TQL_PRIVACY_POLICY_URL,
	TQL_TERMS_URL,
} from "~/shared/constants/legal-links.ts";

import type { PaymentMethodFormErrors } from "../../hooks/use-payment-method-form-validation.ts";
import { AchFormInputs } from "../ach-form-inputs/ach-form-inputs.tsx";
import { BillingCityStateZip } from "../billing-city-state-zip/billing-city-state-zip.tsx";
import { CcFormInputs } from "../cc-form-inputs/cc-form-inputs.tsx";
import {
	FieldErrorMessage,
	fieldErrorWrapperClass,
} from "../field-error/field-error.tsx";
import styles from "./new-payment-method-form.module.css";

export const NewPaymentMethodForm = ({
	paymentType,
	newPaymentValues,
	handleSetNewPaymentValue,
	errors,
}: {
	paymentType: "ACH" | "CreditCard" | null;
	newPaymentValues: {
		cardholderName: string;
		bankName: string;
		bankAccountNumber: string;
		routingNumber: string;
		accountHolderName: string;
		ownershipType: string;
		accountType: string;
		billingAddress1: string;
		billingAddress2: string;
		city: string;
		state: string;
		country: string;
		zip: string;
		isDefault: boolean;
	};
	handleSetNewPaymentValue: (field: string, value: string | boolean) => void;
	errors: PaymentMethodFormErrors;
}) => {
	const legalText = () => {
		if (paymentType === "CreditCard") {
			return (
				<p>
					By clicking &quot;Add Payment Method&quot;, I authorize TQL to store
					my account information on file for future use. Further, by clicking
					&quot;Add Payment Method&quot;, I consent and agree to TQL&apos;s{" "}
					<a href={TQL_PRIVACY_POLICY_URL} rel={"noreferrer"} target={"_blank"}>
						Privacy and Cookies Policy
					</a>{" "}
					and{" "}
					<a href={TQL_TERMS_URL} rel={"noreferrer"} target={"_blank"}>
						Terms of Use
					</a>
					.
				</p>
			);
		}
		return (
			<>
				<p>
					I agree and consent to TQL&apos;s{" "}
					<a href={TQL_PRIVACY_POLICY_URL} rel={"noreferrer"} target={"_blank"}>
						Privacy and Cookies Policy
					</a>{" "}
					and{" "}
					<a href={TQL_TERMS_URL} rel={"noreferrer"} target={"_blank"}>
						Terms of Use.
					</a>{" "}
				</p>
				{paymentType === "ACH" && (
					<p>
						By clicking &quot;Add Payment Method&quot;, I authorize Braintree, a
						service of PayPal, on behalf of Total Quality Logistics, LLC, to
						verify my bank account information using bank information and
						consumer reports, as applicable.
					</p>
				)}
			</>
		);
	};
	return (
		<div>
			{paymentType === "CreditCard" ? (
				<CcFormInputs
					errors={errors}
					handleSetNewPaymentValue={handleSetNewPaymentValue}
					newPaymentValues={newPaymentValues}
				/>
			) : (
				<AchFormInputs
					errors={errors}
					handleSetNewPaymentValue={handleSetNewPaymentValue}
					newPaymentValues={newPaymentValues}
				/>
			)}
			<div className={styles.billingSection}>
				<h4>Billing Address Info:</h4>
				<div className={styles.field}>
					<Label>
						Street Address Line 1 <span className={"required"}>&nbsp;*</span>
					</Label>
					<div
						className={
							errors.billingAddress1 ? fieldErrorWrapperClass : undefined
						}
					>
						<Input
							onChange={(e) =>
								handleSetNewPaymentValue(
									"billingAddress1",
									e.target.value as string,
								)
							}
							value={newPaymentValues.billingAddress1}
						/>
					</div>
					{errors.billingAddress1 ? <FieldErrorMessage /> : null}
				</div>
				<div className={styles.field}>
					<Label>Street Address Line 2</Label>
					<Input
						onChange={(e) =>
							handleSetNewPaymentValue(
								"billingAddress2",
								e.target.value as string,
							)
						}
						value={newPaymentValues.billingAddress2}
					/>
				</div>
				<BillingCityStateZip
					errors={errors}
					handleSetNewPaymentValue={handleSetNewPaymentValue}
					newPaymentValues={newPaymentValues}
				/>
			</div>
			<div className={styles.defaultPaymentRow}>
				<Checkbox
					checked={newPaymentValues.isDefault}
					label={"Set as Default Payment Method"}
					onChange={(e) => handleSetNewPaymentValue("isDefault", e.value)}
				/>
			</div>
			{legalText()}
		</div>
	);
};
